// Loader for the per-map traversability grid used by the map-control overlay.
//
// The grids are derived offline from the CS2 nav mesh (see tasks/map-control-plan.md). Only the
// derived grid ships — no Valve map data. Assets are imported as URLs the same way the radar PNGs
// are in content/maps/mapData.ts, so webpack fingerprints and caches them.

import de_dust2 from '../../../../content/maps/grids/de_dust2.psmg';
import de_mirage from '../../../../content/maps/grids/de_mirage.psmg';
import de_inferno from '../../../../content/maps/grids/de_inferno.psmg';
import de_ancient from '../../../../content/maps/grids/de_ancient.psmg';
import de_anubis from '../../../../content/maps/grids/de_anubis.psmg';
import de_overpass from '../../../../content/maps/grids/de_overpass.psmg';
import de_train from '../../../../content/maps/grids/de_train.psmg';
import de_nuke from '../../../../content/maps/grids/de_nuke.psmg';
import de_vertigo from '../../../../content/maps/grids/de_vertigo.psmg';
import de_cache from '../../../../content/maps/grids/de_cache.psmg';
// de_nuke is split per floor: one 2D grid would collapse upper and lower onto the same XY.
import de_nuke_upper from '../../../../content/maps/grids/de_nuke_upper.psmg';
import de_nuke_lower from '../../../../content/maps/grids/de_nuke_lower.psmg';

const GRID_URLS: Record<string, string> = {
    de_dust2, de_mirage, de_inferno, de_ancient, de_anubis,
    de_overpass, de_train, de_nuke, de_vertigo, de_cache,
    de_nuke_upper, de_nuke_lower,
};

export interface ControlGrid {
    /** Grid width/height in cells (square). */
    G: number;
    /** Cell size in world units. */
    cell: number;
    /** Radar transform, matching mapConfigs in content/maps/mapData.ts. */
    cfg: { pos_x: number; pos_y: number; scale: number };
    /** 1 byte per cell: bit0 walkable, bits 1-4 passable N,E,S,W. */
    data: Uint8Array;
    /**
     * The source nav-mesh polygons in world coordinates, flattened as [x,y,x,y,...] per polygon.
     * The grid drives pathfinding; these drive rendering, so regions follow real map geometry
     * instead of stair-stepping along cell edges.
     */
    polygons: Int16Array[];
    /** Nav-polygon index covering each grid cell, 0xFFFF where none. Empty before format v3. */
    cellPoly: Uint16Array;
    /**
     * Precomputed polygon-to-polygon visibility, `pvsStride` bytes per polygon. Bit p of row q is
     * set when polygons q and p can see each other. Derived offline by raycasting the map's real
     * geometry, which is the only way to express "a gun trained down this lane".
     */
    pvs: Uint8Array;
    pvsStride: number;
}

const cache = new Map<string, Promise<ControlGrid | null>>();

export function hasControlGrid(mapName: string): boolean {
    return !!GRID_URLS[mapName];
}

/** Loads and parses a map's grid. Cached per map; returns null if the map has no grid. */
export function loadControlGrid(mapName: string): Promise<ControlGrid | null> {
    const cached = cache.get(mapName);
    if (cached) return cached;

    const url = GRID_URLS[mapName];
    if (!url) return Promise.resolve(null);

    const p = fetch(url)
        .then(async (res) => {
            if (!res.ok) return null;
            const buf = new Uint8Array(await res.arrayBuffer());
            if (buf.length < 20) return null;
            const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
            // "PSMG"
            if (buf[0] !== 0x50 || buf[1] !== 0x53 || buf[2] !== 0x4d || buf[3] !== 0x47) return null;
            const version = buf[4];
            if (version < 1 || version > 3) return null;
            const cell = buf[5];
            const G = dv.getUint16(6, true);
            const cfg = {
                pos_x: dv.getFloat32(8, true),
                pos_y: dv.getFloat32(12, true),
                scale: dv.getFloat32(16, true),
            };
            if (buf.length < 20 + G * G) return null;
            const data = buf.subarray(20, 20 + G * G);

            const polygons: Int16Array[] = [];
            let cellPoly = new Uint16Array(0);
            let pvs = new Uint8Array(0);
            let pvsStride = 0;

            if (version >= 2) {
                let p = 20 + G * G;
                if (p + 4 <= buf.length) {
                    const count = dv.getUint32(p, true); p += 4;
                    for (let i = 0; i < count && p < buf.length; i++) {
                        const n = buf[p]; p += 1;
                        if (p + n * 4 > buf.length) break;
                        const poly = new Int16Array(n * 2);
                        for (let k = 0; k < n; k++) {
                            poly[k * 2] = dv.getInt16(p, true);
                            poly[k * 2 + 1] = dv.getInt16(p + 2, true);
                            p += 4;
                        }
                        polygons.push(poly);
                    }

                    if (version >= 3 && p + G * G * 2 + 4 <= buf.length) {
                        cellPoly = new Uint16Array(G * G);
                        for (let c = 0; c < G * G; c++) cellPoly[c] = dv.getUint16(p + c * 2, true);
                        p += G * G * 2;
                        pvsStride = dv.getUint32(p, true); p += 4;
                        const need = polygons.length * pvsStride;
                        if (p + need <= buf.length) pvs = buf.subarray(p, p + need);
                        else { pvsStride = 0; cellPoly = new Uint16Array(0); }
                    }
                }
            }
            return { G, cell, cfg, data, polygons, cellPoly, pvs, pvsStride };
        })
        .catch(() => null);

    cache.set(mapName, p);
    return p;
}
